<template>
	<MapLibre
		ref="mapRef"
		:auto-init="isDayViewActive()"
		:locations="mapLocations"
		:selected-date="selectedDate"
		location-id-key="id"
		location-title-key="label"
		:tool-box="toolBox"
		:bottom-tool-box="bottomToolBox"
		:detail-marker-renderer="renderMarkerDetail"
		@toolbox-action="onToolboxAction"
		@filters-changed="onFiltersChanged"
		@edit-delivery-location="handleEditDeliveryLocation"
		:need-filter="needFilter"
		@add-new-visit="handleAddNewVisit"
		@phone-number-copied="handlePhoneNumberCopied"
		:enable-marker-drag="true"
		@marker-drag-drop="onMarkerDragDrop"
	/>
	<AppLoader :visible="mapUpdating" />
	<NavigationAppSheet v-model="navigationSheetOpen" :stops="navigationStops" />
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { iframeDataBridge } from '@/iframe/iframeDataBridge';
import { getDeliveryLocationTypeIcon, getDeliveryLocationTypeColor, handleEditDeliveryLocation, handleAddNewVisit, handlePhoneNumberCopied, getDeliveryLocationKPIs, getLastVisitInfo, getVisitFrequency, resetDetailMarkerData } from '@/utils/domain/appointmentUtils';
import { appointmentService } from '@/services/appointmentService';
import { useTranslator } from '@/composables/useTranslator';
import { getDayRouteStops, planDayRoute, getNextStop, geoPointLookup, positionToPoint, pointToPosition } from '@/utils/domain/dayRoute';
import { isSameDay } from 'date-fns';
import { eventBus } from '@/utils/core/eventBus.js';
import MapLibre from './MapLibre.vue';
import NavigationAppSheet from './NavigationAppSheet.vue';
import AppLoader from '@/components/common/AppLoader.vue';
import { useFilterStore } from '@/stores/FilterStore';

defineProps({
	needFilter: { type: Boolean, default: false },
});

const t = useTranslator();
const mapRef = ref(null);
const mapLocations = ref([]);
const baseMapLocations = ref([]);
const selectedDate = ref(null);
const toolBox = ref(['allDeliveryLocations']);
const bottomToolBox = ref(['goToNextAppointment', 'seeAllRoutes', 'openInApp']);
const allDeliveryLocationsActive = ref(false);
const isFilterActive = ref(false);
const navigationStops = ref([]);
const navigationSheetOpen = ref(false);
const mapUpdating = ref(false);
let mapUpdatingSafetyTimer = null;
let mapInitialized = false;
let mapBusyToken = 0;
let fixedLocationsToken = 0;
let dayRouteToken = 0;
let dayNavigation = [];
const filterStore = useFilterStore();

function hasActiveFilter() {
	return (mapRef.value?.isFilterDirty?.() ?? isFilterActive.value) || filterStore.hasFilters;
}

function showMapUpdating() {
	mapBusyToken++;
	mapUpdating.value = true;
	if (mapUpdatingSafetyTimer) clearTimeout(mapUpdatingSafetyTimer);
	mapUpdatingSafetyTimer = setTimeout(() => {
		mapUpdating.value = false;
		mapUpdatingSafetyTimer = null;
	}, 10000);
}

function hideMapUpdating(expectedToken = null) {
	if (expectedToken !== null && expectedToken !== mapBusyToken) return;
	mapUpdating.value = false;
	if (mapUpdatingSafetyTimer) {
		clearTimeout(mapUpdatingSafetyTimer);
		mapUpdatingSafetyTimer = null;
	}
}

const iframeWin = iframeDataBridge.getIframeWindow();
const amplify = iframeWin?.amplify;
const storage = iframeDataBridge.getStorage();
const user = iframeDataBridge.getCurrentUser();
const enumAppointment = iframeDataBridge.enumAppointment;
const sfaHelper = iframeDataBridge.getSfaHelper();

// The agenda map is only meaningful in day view with the map toggled on. Used both to gate the
// initial map instantiation (MapLibre :auto-init) and the initial data push in onMounted, so the
// map's WebGL context / tile fetches never spin up in week/month view.
function isDayViewActive() {
	const vm = iframeWin?.externis?.sales?.vmAgenda;
	return vm?.calendar?.view?.type === 'timeGridDay' && !!vm?.isMapActive;
}

function onCalendarMapBusy() {
	if (isDayViewActive()) showMapUpdating();
}

function getGeoVisits(visits, geoInfos) {
	return visits.filter((v) => geoInfos?.some((g) => g.deliveryLocationId === v.deliveryLocationId && g.latitude && g.longitude));
}

function getVisitSequences(visits) {
	const positions = new Map();
	visits.forEach((v, index) => {
		if (!positions.has(v.deliveryLocationId)) positions.set(v.deliveryLocationId, []);
		positions.get(v.deliveryLocationId).push(index + 1);
	});

	const sequences = new Map();
	positions.forEach((list, id) => {
		const first = list[0];
		const last = list[list.length - 1];
		sequences.set(id, first === last ? first : `${first}-${last}`);
	});
	return sequences;
}

async function onCalendarMapUpdate({ deliveryLocations, date }) {
	// This event now only fires in day view with the map on (see agenda.js enableMapWidget). Rebuild
	// the map instance if it was torn down while the user was in week/month view. No-op if alive.
	mapRef.value?.reinitMap?.();
	if (mapInitialized) showMapUpdating();
	const startToken = mapBusyToken;
	selectedDate.value = date ? new Date(date) : null;

	mapRef.value?.clearRoutes?.();
	mapRef.value?.hideDetailMarker?.();
	dayRouteToken++;
	dayNavigation = [];
	navigationStops.value = [];

	if (!deliveryLocations?.length) {
		baseMapLocations.value = [];
		mapLocations.value = [];
		if (allDeliveryLocationsActive.value) {
			await applyAllDeliveryLocations();
		}
		await nextTick();
		await centerMapWithNoScheduledVisits();

		if (hasActiveFilter() && mapLocations.value.length > 0) {
			await mapRef.value?.applyFilters?.(mapLocations.value);
		}

		mapInitialized = true;
		hideMapUpdating(startToken);
		return;
	}

	const geoInfos = await new Promise((resolve) => {
		storage.get('DELIVERYLOCATIONGEO', resolve);
	});

	const locations = deliveryLocations
		.map((dl) => {
			const geoInfo = geoInfos?.find((g) => g.deliveryLocationId === dl.id);
			if (!geoInfo?.latitude || !geoInfo?.longitude) return null;

			return {
				id: dl.id,
				label: dl.label,
				companyName: dl.companyName,
				deliveryLocationTypeId: dl.deliveryLocationTypeId,
				accountId: dl.accountId,
				channelId: dl.channelId,
				groupId: dl.groupId,
				POSTypeId: dl.POSTypeId,
				distributionChannel: dl.distributionChannel,
				phoneNumber: dl.phoneNumber || null,
				addressLine: dl.addressLine,
				addressLine2: dl.addressLine2,
				city: dl.city,
				postcode: dl.postcode,
				country: dl.country,
				icon: getDeliveryLocationTypeIcon(dl),
				color: getDeliveryLocationTypeColor(dl),
				_geo: { latitude: geoInfo.latitude, longitude: geoInfo.longitude },
				lat: geoInfo.latitude,
				lng: geoInfo.longitude,
			};
		})
		.filter(Boolean);

	const sfaAgendaVM = iframeWin?.externis?.sales?.vmAgenda;
	const routingVisits = sfaAgendaVM?.getVisitsByDay?.(date ? new Date(date) : null, true) || [];

	// Sort by chronological visit order before assigning sequence badges.
	// deliveryLocations arrives in ecosVM order, not visit time order.
	const visitOrder = routingVisits.map((v) => v.deliveryLocationId);
	locations.sort((a, b) => {
		const ai = visitOrder.indexOf(a.id);
		const bi = visitOrder.indexOf(b.id);
		if (ai === -1 && bi === -1) return 0;
		if (ai === -1) return 1;
		if (bi === -1) return -1;
		return ai - bi;
	});
	const geoVisits = getGeoVisits(routingVisits, geoInfos);
	const sequences = getVisitSequences(geoVisits);
	locations.forEach((loc, index) => {
		loc.sequence = sequences.get(loc.id) ?? index + 1;
	});

	const fixedLocations = await addFixedLocations(locations, { geoInfos, routingVisits });

	baseMapLocations.value = fixedLocations;
	mapLocations.value = fixedLocations;

	if (allDeliveryLocationsActive.value) {
		await applyAllDeliveryLocations();
	}

	if (hasActiveFilter()) {
		await mapRef.value?.applyFilters?.(mapLocations.value);
	}

	if (locations.length > 0) {
		await nextTick();
		await drawDayRoute(date, geoInfos);
	}

	mapInitialized = true;
	hideMapUpdating(startToken);
}

function getDayRoute(date, geoInfos) {
	const dayStart = new Date(date);
	dayStart.setHours(0, 0, 0, 0);

	return getDayRouteStops({
		appointments: iframeWin?.externis?.sales?.vmAgenda?.getVisitsByDay?.(dayStart, true) || [],
		dayStart,
		getPoint: geoPointLookup(geoInfos),
		homeId: user.deliveryLocationHomeId,
		routing: sfaHelper.getRouting(),
		enumAppointment,
	});
}

async function drawDayRoute(date, geoInfos) {
	if (!date) return;
	const token = ++dayRouteToken;
	const dayRoute = getDayRoute(date, geoInfos);
	const origin = isSameDay(new Date(date), new Date()) ? positionToPoint(await mapRef.value?.getLivePosition?.()) : null;
	if (token !== dayRouteToken) return;

	const plan = planDayRoute({ ...dayRoute, origin });
	dayNavigation = plan.navigation;
	navigationStops.value = plan.navigation;
	mapRef.value?.fitCoordinates?.(plan.waypoints);
	await mapRef.value?.drawRoutePlan?.(plan);
}

async function addFixedLocations(sourceLocations, cached = {}) {
	const locations = (sourceLocations || []).filter((l) => !l._fixed);
	const geoInfos =
		cached.geoInfos ||
		(await new Promise((resolve) => {
			storage.get('DELIVERYLOCATIONGEO', resolve);
		}));

	let routingVisits = cached.routingVisits;
	if (!routingVisits) {
		const sfaAgendaVM = iframeWin?.externis?.sales?.vmAgenda;
		const viewDate = selectedDate.value || sfaAgendaVM?.calendar?.view?.currentStart;
		routingVisits = sfaAgendaVM?.getVisitsByDay?.(viewDate ? new Date(viewDate) : null, true) || [];
	}

	const isStartNight = routingVisits.length > 0 && routingVisits[0].subType === enumAppointment?.getSubTypeId?.('NIGHT');
	const isEndNight = routingVisits.length > 0 && routingVisits[routingVisits.length - 1].subType === enumAppointment?.getSubTypeId?.('NIGHT');
	const homeGeoInfo = user.deliveryLocationHomeId ? geoInfos?.find((g) => g.deliveryLocationId === user.deliveryLocationHomeId) : null;

	const ecosVM = iframeDataBridge.getEcosVM();
	// Add home marker at start/end of the location list when applicable
	if (homeGeoInfo?.latitude && homeGeoInfo?.longitude) {
		const homeDL = (ecosVM?.deliveryLocations || []).find((dl) => dl.id === user.deliveryLocationHomeId);
		if (homeDL) {
			const homeMarker = {
				id: homeDL.id,
				label: homeDL.label,
				companyName: homeDL.companyName,
				deliveryLocationTypeId: homeDL.deliveryLocationTypeId,
				accountId: homeDL.accountId,
				channelId: homeDL.channelId,
				groupId: homeDL.groupId,
				POSTypeId: homeDL.POSTypeId,
				distributionChannel: homeDL.distributionChannel,
				phoneNumber: homeDL.phoneNumber || null,
				addressLine: homeDL.addressLine,
				addressLine2: homeDL.addressLine2,
				city: homeDL.city,
				postcode: homeDL.postcode,
				country: homeDL.country,
				icon: 'home',
				color: getDeliveryLocationTypeColor(homeDL),
				_geo: { latitude: homeGeoInfo.latitude, longitude: homeGeoInfo.longitude },
				lat: homeGeoInfo.latitude,
				lng: homeGeoInfo.longitude,
				_fixed: true,
			};
			const hasStartHome = locations.length > 0 && locations[0].id === user.deliveryLocationHomeId;
			const hasEndHome = locations.length > 0 && locations[locations.length - 1].id === user.deliveryLocationHomeId;
			if (!isStartNight && !hasStartHome) locations.unshift(homeMarker);
			if (!isEndNight && !hasEndHome) locations.push(homeMarker);
		}
	}

	// If the first or last visit is a night hotel and it's missing from locations (excluded from deliveryLocations),
	// fetch it from ecosVM and add it explicitly.
	if (isStartNight || isEndNight) {
		const sequences = getVisitSequences(getGeoVisits(routingVisits, geoInfos));
		const nightDLIds = [...new Set([isStartNight ? routingVisits[0].deliveryLocationId : null, isEndNight ? routingVisits[routingVisits.length - 1].deliveryLocationId : null].filter(Boolean))];
		for (const nightDLId of nightDLIds) {
			if (locations.some((l) => l.id === nightDLId)) continue;
			const nightDL = (ecosVM?.deliveryLocations || []).find((dl) => dl.id === nightDLId);
			const nightGeo = geoInfos?.find((g) => g.deliveryLocationId === nightDLId);
			if (nightDL && nightGeo?.latitude && nightGeo?.longitude) {
				locations.push({
					id: nightDL.id,
					label: nightDL.label,
					companyName: nightDL.companyName,
					deliveryLocationTypeId: nightDL.deliveryLocationTypeId,
					accountId: nightDL.accountId,
					channelId: nightDL.channelId,
					groupId: nightDL.groupId,
					POSTypeId: nightDL.POSTypeId,
					distributionChannel: nightDL.distributionChannel,
					phoneNumber: nightDL.phoneNumber || null,
					addressLine: nightDL.addressLine,
					addressLine2: nightDL.addressLine2,
					city: nightDL.city,
					postcode: nightDL.postcode,
					country: nightDL.country,
					icon: 'hotel',
					color: getDeliveryLocationTypeColor(nightDL),
					_geo: { latitude: nightGeo.latitude, longitude: nightGeo.longitude },
					lat: nightGeo.latitude,
					lng: nightGeo.longitude,
					sequence: sequences.get(nightDLId) ?? locations.length + 1,
					_fixed: true,
				});
			}
		}
	}
	return locations;
}

function onMarkerDragDrop({ markerData }) {
	const document = iframeDataBridge.getIframeWindow().document;
	const dropEl = document.elementFromPoint(event.clientX, event.clientY);

	let currentEl = dropEl;
	let loop = 0;
	while (currentEl && !currentEl.getAttribute('data-time') && loop < 5) {
		currentEl = currentEl.parentElement;
		loop++;
	}

	const timeAttr = currentEl?.getAttribute('data-time');
	const dropTime = timeAttr ? new Date(`1970-01-01T${timeAttr}`) : new Date();
	const dropDate = selectedDate.value ? new Date(selectedDate.value) : new Date();
	dropDate.setHours(dropTime.getHours(), dropTime.getMinutes(), dropTime.getSeconds());

	handleAddNewVisit({
		deliveryLocationId: markerData.id,
		deliveryLocationName: markerData.label || markerData.title || '',
		slot: {
			start: dropDate,
			end: new Date(dropDate.getTime() + 60 * 60 * 1000),
		},
	});
}

function onFiltersChanged(filteredLocations) {
	isFilterActive.value = mapLocations.value.length > 0 && (filteredLocations || []).length < mapLocations.value.length;
	const token = ++fixedLocationsToken;
	addFixedLocations(filteredLocations).then((result) => {
		if (token !== fixedLocationsToken) return;
		mapLocations.value = result || [];
	});
}

function getAuthorizedOrganisationIds(ecosVM) {
	const currentViewModel = iframeWin?.externis?.ecosVM?.currentViewModel;
	const selectedUser = currentViewModel?.selectedUser?.();
	if (!selectedUser) return null;

	const getChildren = iframeWin?.hierarchyHelper?.getChildren;
	const orgAuthorized = (getChildren?.(ecosVM.organisations, selectedUser.organisationId) || []).map((o) => o.id);
	orgAuthorized.push(selectedUser.organisationId);

	const isOrganizationShared = (ecosVM.organisationSharings || []).some((s) => (s.parentOrganisationId === user.organisationId && s.organisations?.includes(selectedUser.organisationId)) || (s.parentOrganisationId === selectedUser.organisationId && s.organisations?.includes(user.organisationId)));
	if (isOrganizationShared) {
		orgAuthorized.push(user.organisationId, ...(getChildren?.(ecosVM.organisations, user.organisationId) || []).map((o) => o.id));
	}

	return orgAuthorized;
}

async function applyAllDeliveryLocations() {
	const ecosVM = iframeDataBridge.getEcosVM();
	const authorizedOrgIds = getAuthorizedOrganisationIds(ecosVM);
	const allDLs = (ecosVM.deliveryLocations || []).filter((dl) => !authorizedOrgIds || (dl.organisations || []).some((o) => authorizedOrgIds.includes(o)));

	const geoInfos = await new Promise((resolve) => {
		storage.get('DELIVERYLOCATIONGEO', resolve);
	});
	const extra = allDLs
		.filter((dl) => !baseMapLocations.value.find((b) => b.id === dl.id))
		.map((dl) => {
			const geoInfo = geoInfos?.find((g) => g.deliveryLocationId === dl.id);
			if (!geoInfo?.latitude || !geoInfo?.longitude) return null;
			return {
				id: dl.id,
				label: dl.label,
				companyName: dl.companyName,
				deliveryLocationTypeId: dl.deliveryLocationTypeId,
				accountId: dl.accountId,
				channelId: dl.channelId,
				groupId: dl.groupId,
				POSTypeId: dl.POSTypeId,
				distributionChannel: dl.distributionChannel,
				phoneNumber: dl.phoneNumber || null,
				addressLine: dl.addressLine,
				addressLine2: dl.addressLine2,
				city: dl.city,
				postcode: dl.postcode,
				country: dl.country,
				icon: getDeliveryLocationTypeIcon(dl),
				color: getDeliveryLocationTypeColor(dl),
				_geo: { latitude: geoInfo.latitude, longitude: geoInfo.longitude },
				lat: geoInfo.latitude,
				lng: geoInfo.longitude,
			};
		})
		.filter(Boolean);
	mapLocations.value = [...baseMapLocations.value, ...extra];
}

const PARIS_CENTER = [2.3488, 48.8534];
const EUROPE_ZOOM = 3.5;
const USER_POSITION_ZOOM = 14;

async function centerMapWithNoScheduledVisits() {
	await mapRef.value?.refreshUserPosition?.();
	const userPosition = mapRef.value?.isGeolocationActive?.() ? mapRef.value.getUserPosition() : null;

	if (allDeliveryLocationsActive.value) {
		await applyAllDeliveryLocations();
		await nextTick();
		if (mapLocations.value.length > 0) {
			mapRef.value?.fitAllWithUserPosition?.();
			return;
		}
	}

	if (userPosition?.lat && userPosition?.lng) {
		mapRef.value?.setCenterZoom?.([userPosition.lng, userPosition.lat], USER_POSITION_ZOOM);
		return;
	}

	if (!allDeliveryLocationsActive.value) {
		allDeliveryLocationsActive.value = true;
		const tool = mapRef.value?.getToolState?.('allDeliveryLocations');
		if (tool) {
			tool.active = true;
			tool.tooltip = t('RemoveAllDeliveryLocations');
		}
		await applyAllDeliveryLocations();
		await nextTick();
	}

	if (mapLocations.value.length > 0) {
		mapRef.value?.fitAllWithUserPosition?.();
		return;
	}

	mapRef.value?.setCenterZoom?.(PARIS_CENTER, EUROPE_ZOOM);
}

async function onToolboxAction({ toolId, active }) {
	if (toolId === 'allDeliveryLocations') {
		allDeliveryLocationsActive.value = active;
		if (!active) {
			mapLocations.value = baseMapLocations.value.slice();
		} else {
			await applyAllDeliveryLocations();
		}
		await nextTick();
		setTimeout(() => mapRef.value?.applyFilters?.(mapLocations.value), 300);
		setTimeout(() => mapRef.value?.fitAllWithUserPosition?.(), 600);
	} else if (toolId === 'goToNextAppointment') {
		await handleGoToNextAppointment();
	} else if (toolId === 'seeAllRoutes') {
		await handleSeeAllRoutes();
	} else if (toolId === 'openInApp') {
		handleOpenInApp();
	} else if (toolId === 'clearRoutes') {
		navigationStops.value = dayNavigation;
	}
}

function getViewDate() {
	return selectedDate.value || iframeWin?.externis?.sales?.vmAgenda?.calendar?.view?.currentStart || null;
}

async function handleGoToNextAppointment() {
	const date = getViewDate();
	if (!date) return;

	const geoInfos = await new Promise((resolve) => storage.get('DELIVERYLOCATIONGEO', resolve));
	const nextStop = getNextStop(getDayRoute(date, geoInfos));
	if (!nextStop) return;

	const ecosVM = iframeDataBridge.getEcosVM();
	const label = (ecosVM?.deliveryLocations || []).find((dl) => dl.id === nextStop.appointment.deliveryLocationId)?.label || '';

	dayRouteToken++;
	const waypoints = await mapRef.value?.navigateToLocation?.(nextStop.point[1], nextStop.point[0], label, pointToPosition(nextStop.fallbackOrigin));
	if (waypoints) navigationStops.value = [nextStop.point];
}

async function handleSeeAllRoutes() {
	const date = getViewDate();
	if (!date) return;

	const geoInfos = await new Promise((resolve) => storage.get('DELIVERYLOCATIONGEO', resolve));
	await drawDayRoute(date, geoInfos);
}

function handleOpenInApp() {
	if (navigationStops.value.length) navigationSheetOpen.value = true;
}

async function renderMarkerDetail(markerData, _iframeDocument, detailMarkerData) {
	resetDetailMarkerData(detailMarkerData, markerData);

	let appointmentData = null;
	const deliveryLocation = markerData;

	await appointmentService
		.getAppointments({ deliveryLocationId: deliveryLocation.id })
		.then((appointments) => {
			if (appointments && appointments.length > 0) {
				appointmentData = appointments[0];
			}
		})
		.catch(() => {
			appointmentData = null;
		});

	const ecosVM = iframeDataBridge.getEcosVM();
	const deliveryLocationId = deliveryLocation?.id || null;
	detailMarkerData.value.title = markerData.label || '';
	detailMarkerData.value.id = markerData.id || null;
	detailMarkerData.value.phoneNumber = deliveryLocation.phoneNumber || null;
	detailMarkerData.value.lat = markerData.lat || markerData.latitude || appointmentData?.lat || deliveryLocation?.lat || null;
	detailMarkerData.value.lng = markerData.lng || markerData.longitude || appointmentData?.lng || deliveryLocation?.lng || null;

	const addressParts = [];
	if (deliveryLocation.addressLine) addressParts.push(deliveryLocation.addressLine);
	if (deliveryLocation.addressLine2) addressParts.push(deliveryLocation.addressLine2);
	if (deliveryLocation.city) addressParts.push(deliveryLocation.city);
	if (deliveryLocation.postcode) addressParts.push(deliveryLocation.postcode);
	if (deliveryLocation.country) addressParts.push(deliveryLocation.country);
	detailMarkerData.value.address = addressParts.join(', ');
	detailMarkerData.value.lastVisitComment = '';

	if (deliveryLocationId) {
		try {
			const lastVisit = appointmentData ? await getLastVisitInfo(deliveryLocationId, appointmentData.startDate || null) : null;
			const lastComment = lastVisit && lastVisit.comment && lastVisit.comment.trim() ? lastVisit.comment : '';
			detailMarkerData.value.lastVisitComment = lastComment;
		} catch (e) {
			detailMarkerData.value.lastVisitComment = '';
		}
	}

	detailMarkerData.value.sps = appointmentData?.sps ?? null;
	detailMarkerData.value.accountLogo = null;
	detailMarkerData.value.accountName = '';

	if (markerData.accountId) {
		const account = ecosVM.accounts && typeof ecosVM.accounts === 'function' ? ecosVM.accounts().find((a) => a.id === markerData.accountId) : (ecosVM.accounts || []).find((a) => a.id === markerData.accountId);

		if (account) {
			detailMarkerData.value.accountName = account.Name || account.name || '';
			const imageUId = account.imageUId || account.ImageUId || null;
			if (imageUId && imageUId !== '00000000-0000-0000-0000-000000000000') {
				try {
					if (iframeDataBridge.isOnline()) {
						const { getAccountImageUrl } = await import('@/api/core/file');
						const imageUrl = await getAccountImageUrl(imageUId);
						detailMarkerData.value.accountLogo = imageUrl || null;
					}
				} catch (e) {
					detailMarkerData.value.accountLogo = null;
				}
			}
		}
	}

	const fields = {};
	const deliveryLocationTypes = ecosVM.deliveryLocationTypes || [];
	const deliveryLocationType = deliveryLocationTypes.find((type) => type.id === deliveryLocation.deliveryLocationTypeId);
	if (deliveryLocationType) {
		fields[t('Type')] = deliveryLocationType.name || deliveryLocationType.reference || '';
	}

	if (appointmentData?.startDate) {
		const startDate = new Date(appointmentData.startDate);
		fields[t('Date')] = startDate.toLocaleDateString();
		if (appointmentData.endDate) {
			const endDate = new Date(appointmentData.endDate);
			fields[t('Hours')] = `${startDate.toLocaleTimeString().substring(0, 5)} - ${endDate.toLocaleTimeString().substring(0, 5)}`;
		}
	}

	if (deliveryLocationId) {
		try {
			const frequency = await getVisitFrequency(deliveryLocationId);
			if (frequency) fields[t('CurrentVisitRate')] = frequency;
			const kpis = appointmentData ? await getDeliveryLocationKPIs(deliveryLocationId, appointmentData) : [];
			detailMarkerData.value.kpis = Array.isArray(kpis) ? kpis : [];
		} catch (e) {
			detailMarkerData.value.kpis = [];
		}
	} else {
		detailMarkerData.value.kpis = [];
	}

	if (detailMarkerData.value.address) {
		fields[t('Address')] = detailMarkerData.value.address;
	}
	detailMarkerData.value.fields = fields;
}

function onPersistentZoneReplaced({ selector } = {}) {
	if (selector && selector !== 'vue-calendar-map-zone') return;
	nextTick(() => {
		mapRef.value?.resize?.();
	});
}

function onCalendarMapDestroy() {
	mapRef.value?.destroyMap?.();
}

onMounted(() => {
	if (amplify) {
		amplify.subscribe('evtCalendarMapUpdate', onCalendarMapUpdate);
		amplify.subscribe('evtCalendarMapBusy', onCalendarMapBusy);
		amplify.subscribe('evtCalendarMapDestroy', onCalendarMapDestroy);

		nextTick(() => {
			const sfaAgendaVM = iframeWin?.externis?.sales?.vmAgenda;
			// Only push the initial map data (and thus spin up the map) when the agenda is actually in
			// day view with the map on — otherwise the map stays unmounted in week/month view.
			if (sfaAgendaVM?.callCalendarMapEvent && isDayViewActive()) {
				const date = sfaAgendaVM.calendar?.view?.currentStart;
				const routingVisits = sfaAgendaVM.getVisitsByDay?.(date, true) || [];
				const selectedDLIds = routingVisits.map((v) => v.deliveryLocationId).filter(Boolean);
				sfaAgendaVM.callCalendarMapEvent(selectedDLIds, date);
			}
		});
	}

	eventBus.on('vue-persistent-zone-replaced', onPersistentZoneReplaced);
});

onBeforeUnmount(() => {
	if (amplify) {
		amplify.unsubscribe('evtCalendarMapUpdate', onCalendarMapUpdate);
		amplify.unsubscribe('evtCalendarMapBusy', onCalendarMapBusy);
		amplify.unsubscribe('evtCalendarMapDestroy', onCalendarMapDestroy);
	}
	eventBus.off('vue-persistent-zone-replaced', onPersistentZoneReplaced);
	hideMapUpdating();
});
</script>
